import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Table, Alert, Container } from "react-bootstrap";
import { collection, getDocs, query, where, getDoc, doc } from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

const Invoice = () => {
  const [user, setUser] = useState(null); // Logged-in user
  const [userRole, setUserRole] = useState(null); // Role of the user
  const [permissions, setPermissions] = useState(null); // User permissions
  const [accessDenied, setAccessDenied] = useState(false); // Access control
  const [invoices, setInvoices] = useState([]); // Store user-specific invoices
  const [selectedInvoice, setSelectedInvoice] = useState(null); // Currently viewed invoice
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(""); // Error state

  // Fetch role and permissions
  const fetchUserRoleAndPermissions = async (uid) => {
    try {
      const userDocRef = doc(db, "Users", uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const role = userDoc.data().role;
        setUserRole(role);

        if (role === "admin") {
          setPermissions({ canViewInvoice: true }); // Admins always have access
          setAccessDenied(false);
          return;
        }

        const roleDocRef = doc(db, "Roles", role);
        const roleDoc = await getDoc(roleDocRef);

        if (roleDoc.exists()) {
          const rolePermissions = roleDoc.data().permissions;
          setPermissions(rolePermissions);

          if (!rolePermissions.canViewInvoice) {
            setAccessDenied(true);
          } else {
            setAccessDenied(false);
          }
        } else {
          console.error("Role document not found.");
          setAccessDenied(true);
        }
      } else {
        console.error("User document not found.");
        setAccessDenied(true);
      }
    } catch (error) {
      console.error("Error fetching role and permissions:", error);
      setAccessDenied(true);
    }
  };

  // Fetch invoices for the logged-in user
  const fetchUserInvoices = async (userEmail) => {
    try {
      const invoicesRef = collection(db, "Invoices");
      const userInvoicesQuery = query(invoicesRef, where("user.email", "==", userEmail));
      const invoicesSnapshot = await getDocs(userInvoicesQuery);

      const userInvoices = invoicesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setInvoices(userInvoices);

      // Automatically select the first invoice if available
      if (userInvoices.length > 0) {
        setSelectedInvoice(userInvoices[0]);
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching user invoices:", err);
      setError("Failed to load invoices.");
      setLoading(false);
    }
  };

  // Monitor authentication state and fetch role/permissions
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setLoading(true);
        await fetchUserRoleAndPermissions(currentUser.uid);
        await fetchUserInvoices(currentUser.email);
      } else {
        setUser(null);
        setUserRole(null);
        setPermissions(null);
        setAccessDenied(true);
        setInvoices([]);
        setSelectedInvoice(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Header />
        <h5>Loading Invoices...</h5>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="text-center mt-5">
        <Header />
        <Alert variant="danger">Access Denied: You do not have permission to view invoices.</Alert>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center mt-5">
        <Header />
        <Alert variant="danger">{error}</Alert>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center mt-5">
        <Header />
        <Alert variant="info">Please log in to view your invoices.</Alert>
      </div>
    );
  }

    return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Enhanced Header Section */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="ri-file-text-line fs-1 text-primary me-3"></i>
            <h1 className="display-6 fw-bold text-primary mb-0">Your Invoices</h1>
          </div>
          <p className="lead text-muted">View and manage your invoice details</p>
        </div>

        <Row className="g-4">
          {/* Enhanced Invoice List */}
          <Col md={4}>
            <Card className="border-0 shadow-lg rounded-4 h-100">
              <div className="bg-gradient-primary text-white p-4 rounded-top-4" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <i className="ri-file-list-3-line fs-4 me-3"></i>
                    <h5 className="mb-0 fw-bold">Invoice List</h5>
                  </div>
                  <span className="badge bg-white text-primary px-3 py-2 rounded-pill">
                    {invoices.length} invoices
                  </span>
                </div>
              </div>
              <Card.Body className="p-4">
                {invoices.length > 0 ? (
                  <div className="list-group list-group-flush">
                    {invoices.map((invoice) => (
                      <div
                        key={invoice.id}
                        className={`list-group-item list-group-item-action border-0 rounded-3 mb-2 ${
                          selectedInvoice?.id === invoice.id ? "bg-primary text-white" : "bg-light"
                        }`}
                        onClick={() => setSelectedInvoice(invoice)}
                        style={{ 
                          cursor: "pointer",
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedInvoice?.id !== invoice.id) {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                            e.currentTarget.style.transform = 'translateX(4px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedInvoice?.id !== invoice.id) {
                            e.currentTarget.style.backgroundColor = '';
                            e.currentTarget.style.transform = 'translateX(0)';
                          }
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div className={`rounded-circle p-2 me-3 ${
                            selectedInvoice?.id === invoice.id ? 'bg-white bg-opacity-20' : 'bg-primary bg-opacity-10'
                          }`}>
                            <i className={`ri-file-text-line ${
                              selectedInvoice?.id === invoice.id ? 'text-white' : 'text-primary'
                            }`}></i>
                          </div>
                          <div className="flex-grow-1">
                            <h6 className={`mb-1 fw-semibold ${
                              selectedInvoice?.id === invoice.id ? 'text-white' : 'text-dark'
                            }`}>
                              Invoice #{invoice.id.slice(-8)}
                            </h6>
                            <small className={`${
                              selectedInvoice?.id === invoice.id ? 'text-white opacity-75' : 'text-muted'
                            }`}>
                              Total: ${invoice.totalCost.toFixed(2)}
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <i className="ri-file-list-3-line text-muted" style={{ fontSize: '4rem' }}></i>
                    </div>
                    <h5 className="text-muted mb-3">No Invoices Found</h5>
                    <p className="text-muted">You don't have any invoices yet.</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Enhanced Invoice Details */}
          <Col md={8}>
            {selectedInvoice ? (
              <Card className="border-0 shadow-lg rounded-4">
                <div className="bg-gradient-success text-white p-4 rounded-top-4" 
                     style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <i className="ri-file-text-line fs-4 me-3"></i>
                      <h5 className="mb-0 fw-bold">Invoice Details</h5>
                    </div>
                    <span className="badge bg-white text-success px-3 py-2 rounded-pill">
                      #{selectedInvoice.id.slice(-8)}
                    </span>
                  </div>
                </div>
                <Card.Body className="p-4">
                  {/* Company Header */}
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h3 className="fw-bold text-primary mb-1">Your Company Name</h3>
                      <p className="text-muted mb-0">Professional Services</p>
                    </div>
                    <div className="text-end">
                      <h4 className="fw-bold text-primary mb-0">INVOICE</h4>
                      <small className="text-muted">Document</small>
                    </div>
                  </div>

                  {/* Invoice Information */}
                  <Row className="mb-4">
                    <Col md={6}>
                      <Card className="border-0 bg-light rounded-3">
                        <Card.Body className="p-3">
                          <h6 className="fw-bold text-primary mb-3">
                            <i className="ri-information-line me-2"></i>Invoice Details
                          </h6>
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Order Date:</span>
                            <span className="fw-semibold">
                              {new Date(selectedInvoice.createdAt.seconds * 1000).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Order ID:</span>
                            <span className="fw-semibold">{selectedInvoice.id}</span>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* Items Table */}
                  <div className="table-responsive mb-4">
                    <Table className="table table-hover table-borderless mb-0">
                      <thead className="bg-primary text-white">
                        <tr>
                          <th className="border-0">
                            <i className="ri-hashtag me-2"></i>#
                          </th>
                          <th className="border-0">
                            <i className="ri-shopping-bag-line me-2"></i>Item
                          </th>
                          <th className="border-0">
                            <i className="ri-file-text-line me-2"></i>Description
                          </th>
                          <th className="border-0">
                            <i className="ri-number-1 me-2"></i>Quantity
                          </th>
                          <th className="border-0">
                            <i className="ri-money-dollar-circle-line me-2"></i>Unit Cost
                          </th>
                          <th className="border-0 text-end">
                            <i className="ri-calculator-line me-2"></i>Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedInvoice.items.map((item, index) => (
                          <tr key={index} className="border-bottom">
                            <td className="fw-semibold">{index + 1}</td>
                            <td className="fw-semibold">{item.name}</td>
                            <td className="text-muted">{item.description}</td>
                            <td>
                              <span className="badge bg-primary rounded-pill px-3 py-2">
                                {item.quantity}
                              </span>
                            </td>
                            <td className="fw-semibold">${Number(item.unitCost).toFixed(2)}</td>
                            <td className="text-end fw-bold text-success">
                              ${(Number(item.unitCost) * Number(item.quantity)).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* Totals Section */}
                  <Row>
                    <Col md={6}>
                      <Card className="border-0 bg-light rounded-3">
                        <Card.Body className="p-3">
                          <h6 className="fw-bold text-primary mb-3">
                            <i className="ri-file-text-line me-2"></i>Notes
                          </h6>
                          <p className="text-muted mb-0">
                            {selectedInvoice.notes || "No additional notes provided."}
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="border-0 bg-gradient-light rounded-3" 
                            style={{ background: 'linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                        <Card.Body className="p-3">
                          <h6 className="fw-bold text-primary mb-3">
                            <i className="ri-calculator-line me-2"></i>Invoice Summary
                          </h6>
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Sub-total:</span>
                            <span className="fw-semibold">
                              $
                              {selectedInvoice.items
                                .reduce((acc, item) => acc + Number(item.unitCost) * Number(item.quantity), 0)
                                .toFixed(2)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">VAT ({selectedInvoice.vatRate}%):</span>
                            <span className="fw-semibold">
                              $
                              {(
                                selectedInvoice.items.reduce(
                                  (acc, item) => acc + Number(item.unitCost) * Number(item.quantity),
                                  0
                                ) *
                                (selectedInvoice.vatRate / 100)
                              ).toFixed(2)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted">Additional Costs:</span>
                            <span className="fw-semibold">${selectedInvoice.additionalCosts.toFixed(2)}</span>
                          </div>
                          <hr />
                          <div className="d-flex justify-content-between">
                            <h5 className="fw-bold text-primary mb-0">Total Amount:</h5>
                            <h4 className="fw-bold text-success mb-0">
                              $
                              {(
                                selectedInvoice.items.reduce((acc, item) => acc + Number(item.unitCost) * Number(item.quantity), 0) +
                                selectedInvoice.items.reduce(
                                  (acc, item) => acc + Number(item.unitCost) * Number(item.quantity),
                                  0
                                ) *
                                  (selectedInvoice.vatRate / 100) +
                                Number(selectedInvoice.additionalCosts)
                              ).toFixed(2)} USD
                            </h4>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* Action Buttons */}
                  <div className="d-flex justify-content-center mt-4">
                    <Button
                      variant="primary"
                      size="lg"
                      className="px-5 py-3 rounded-pill fw-semibold shadow-lg"
                      onClick={() => window.print()}
                    >
                      <i className="ri-printer-line me-2"></i>
                      Print Invoice
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            ) : (
              <Card className="border-0 shadow-lg rounded-4">
                <Card.Body className="p-5 text-center">
                  <div className="mb-4">
                    <i className="ri-file-text-line text-muted" style={{ fontSize: '4rem' }}></i>
                  </div>
                  <h5 className="text-muted mb-3">Select an Invoice</h5>
                  <p className="text-muted">Choose an invoice from the list to view its details.</p>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default Invoice;
